import React, { useContext } from 'react'
import { Box } from '@chakra-ui/react'
import {
  propConverter,
  PriceInfoContainer,
  PriceLabel,
  PriceLabelTooltip,
  Price,
  PriceAdditionalInfo,
  formatPrice,
  EcochequeBanner,
  SustainabilityBanner,
  PromoLabel,
  BPlusLabel,
  PROMO_LABEL_CONSTANTS,
  Link
} from 'chemistry'
import { getPriceProps } from '../../../common/price'
import { ProductSchema } from './SchemaOrg/ProductSchema'
import { getProductDescription, getProductGtin, getProductAvailability } from '../../../common/selectors/product'
import { isPraxis, isNL, replaceUnitsInTranslation } from 'common/utils'
import { createLogger } from 'common/log'
import { TranslateContext } from 'client/common/translator'

const logger = createLogger('productDetailsFragmentInfo/ProductPrice')

const getProductInformationSheetDocument = ({ documents, format, lang }) => {
  if (!Array.isArray(documents)) return
  const productInformationSheetDocuments = documents.filter(({ subType }) => subType === 'EPREL_PRODUCT_SHEET')
  if (isPraxis(format)) return productInformationSheetDocuments[0]
  if (isNL(lang)) return productInformationSheetDocuments.find(({ locale }) => locale === 'nl_BE')
  return productInformationSheetDocuments.find(({ locale }) => locale === 'fr_BE')
}

const ProductPrice = props => {
  const t = useContext(TranslateContext)
  const { product, format, lang, isGuest, currentLocation, showPraxisPlusHint, name, host, productId, hideVLP } = props
  const { price, priceProps } = getPriceProps(product, lang, format, isGuest, currentLocation, showPraxisPlusHint, t)
  const {
    labelText,
    percentage,
    oldUnitPrice,
    oldPrice,
    unitPrice,
    packetText,
    showPriceLabel,
    showBPlusLabel,
    units,
    showPriceLabelInfo,
    labelHintText,
    unitsInPacket,
    isEcocheque,
    isSustainability,
    isVLP,
    ecochequeText,
    sustainabilityText,
    errors
  } = propConverter({ ...priceProps, isFeatured: true })

  const showPrice = props.showPrice
  if (errors) {
    logger.error(`PRICE_ERROR, product ${format} ${productId} has corrupted price on pdp`)
  }
  let crossedPrice = oldUnitPrice || oldPrice
  let bigPrice = unitPrice || price || crossedPrice
  let priceAdditionalInfo = `${packetText} `
  let unitsStr = oldUnitPrice || unitPrice ? replaceUnitsInTranslation(units) : null
  let additionalInfoPrice = unitsStr ? price || oldPrice : null
  const productInformationSheetDocument = getProductInformationSheetDocument({
    documents: product?.documents,
    format,
    lang
  })

  if (product?.packagePriceFirst) {
    crossedPrice = oldPrice
    bigPrice = price || crossedPrice
    priceAdditionalInfo = `${replaceUnitsInTranslation(units)} `
    unitsStr = null
    additionalInfoPrice = unitPrice || oldUnitPrice
  }
  return (
    <Box>
      <ProductSchema
        {...{
          ...product,
          name,
          image: product?.mainImage,
          url: `https://${host}${currentLocation}`,
          sku: productId,
          description: getProductDescription(product),
          gtin13: getProductGtin(product),
          availability: getProductAvailability(product?.itemAvailability),
          fullPriceProps: { price: bigPrice, unitPrice, unitsInPacket }
        }}
      />
      {showPrice && (
        <PriceInfoContainer>
          <PriceInfoContainer.Before>
            {showBPlusLabel && (
              <>
                <BPlusLabel text={labelText} />
                <PriceLabelTooltip
                  labelHintText={labelHintText}
                  price={price}
                  percentage={percentage}
                  format={format}
                  oldPrice={oldPrice}
                  isDetail
                />
              </>
            )}
            {isVLP && !showPriceLabel && !hideVLP && (
              <PromoLabel
                variant={PROMO_LABEL_CONSTANTS.VARIANT.alwaysLowPrice}
                format={format}
                lang={lang}
                mb='12px'
              />
            )}
            {showPriceLabel && (
              <PriceLabel text={labelText} percentage={percentage} isDetail>
                {showPriceLabelInfo && (
                  <PriceLabelTooltip
                    labelHintText={labelHintText}
                    price={price}
                    percentage={percentage}
                    format={format}
                    oldPrice={oldPrice}
                    isDetail
                  />
                )}
              </PriceLabel>
            )}
          </PriceInfoContainer.Before>
          <PriceInfoContainer.Main>
            <Price oldPrice={crossedPrice} price={bigPrice} units={unitsStr}>
              {unitPrice && (
                <PriceAdditionalInfo text={priceAdditionalInfo}>
                  <b>{formatPrice(additionalInfoPrice)}</b>
                </PriceAdditionalInfo>
              )}
            </Price>
          </PriceInfoContainer.Main>
          <PriceInfoContainer.After>
            {productInformationSheetDocument && (
              <Link
                mt='8px'
                variant='iconLink'
                href={productInformationSheetDocument.url}
                target='_blank'
                rel='noreferrer'
                aria-label='PDF Doc'
              >
                {t('documents.product_information_sheet')}
              </Link>
            )}
            {isEcocheque && <EcochequeBanner {...{ ecochequeText }} />}
            {isSustainability && <SustainabilityBanner {...{ sustainabilityText }} />}
          </PriceInfoContainer.After>
        </PriceInfoContainer>
      )}
    </Box>
  )
}

export default ProductPrice
